import { AssetImageUploadField } from "@/components/asset-image-upload-field";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Text } from "@/components/ui/Text";
import { useSdkClient } from "@/contexts/Sdk";
import { useRBAC } from "@/hooks/useRBAC";
import type { RemoteSessionClient } from "@gram/client/models/components/remotesessionclient.js";
import type { RemoteSessionIssuer } from "@gram/client/models/components/remotesessionissuer.js";
import type { UserSessionIssuer } from "@gram/client/models/components/usersessionissuer.js";
import { CreateRemoteSessionClientFormTokenEndpointAuthMethod } from "@gram/client/models/components/createremotesessionclientform.js";
import {
  UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat,
  type UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat as AuthAudienceFormat,
} from "@gram/client/models/components/updateremotesessionclientform.js";
import { invalidateAllGetMcpServer } from "@gram/client/react-query/getMcpServer.js";
import { invalidateAllGetRemoteMcpServerScopes } from "@gram/client/react-query/getRemoteMcpServerScopes.js";
import {
  invalidateAllMcpServers,
  useMcpServers,
} from "@gram/client/react-query/mcpServers.js";
import { invalidateAllOrganizationRemoteSessionClient } from "@gram/client/react-query/organizationRemoteSessionClient.js";
import { invalidateAllOrganizationRemoteSessionClients } from "@gram/client/react-query/organizationRemoteSessionClients.js";
import { invalidateAllRemoteSessionClients } from "@gram/client/react-query/remoteSessionClients.js";
import { invalidateAllRemoteSessionIssuer } from "@gram/client/react-query/remoteSessionIssuer.js";
import { invalidateAllRemoteSessionIssuers } from "@gram/client/react-query/remoteSessionIssuers.js";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LegacyCallbackAlert } from "@/pages/remote-identity-providers/clientAlerts";
import {
  ClientAssertionAudienceField,
  ClientCredentialsFields,
  EndpointsFields,
  IssuerUrlField,
  OverridesFields,
} from "./IssuerFormFields";
import {
  clientSecretUpdateValue,
  isPrivateKeyJwtAuthMethod,
  parseScopes,
} from "./issuerFormUtils";
import { useAllRemoteSessionClients } from "@/lib/remote-identity";
import { useIssuerDiscovery } from "./useIssuerDiscovery";
import { IssuerDuplicateWarning } from "./IssuerDuplicateWarning";
import { useIssuerDuplicatePreflight } from "./useIssuerDuplicatePreflight";

export function ModifyRemoteIdentityProviderSheet({
  open,
  onOpenChange,
  userSessionIssuer,
  issuer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userSessionIssuer: UserSessionIssuer;
  issuer: RemoteSessionIssuer;
}): JSX.Element {
  // Fetch every remote_session_client tied to this issuer (across all
  // user_session_issuers in the project), walking every page. We need the
  // unfiltered list to compute which OTHER MCP servers are also using this
  // issuer so the operator gets a heads-up that issuer/endpoint edits will
  // affect them — a single-page fetch would silently undercount once a
  // project crosses the default page size of 50.
  const { items: allClients, isLoading: isLoadingClients } =
    useAllRemoteSessionClients(
      { remoteSessionIssuerId: issuer.id },
      { enabled: open },
    );
  const primaryClient: RemoteSessionClient | undefined = allClients.find(
    (clientRow) =>
      clientRow.userSessionIssuerIds.includes(userSessionIssuer.id),
  );
  const otherUserSessionIssuerIds = useMemo(
    () =>
      new Set(
        allClients
          .flatMap((clientRow) => clientRow.userSessionIssuerIds)
          .filter((id) => id !== userSessionIssuer.id),
      ),
    [allClients, userSessionIssuer.id],
  );

  // Look up the MCP servers in the project that bind any of those "other"
  // user_session_issuers — those are the servers whose authentication will
  // be touched by issuer-level edits in this sheet.
  const { data: serversResult } = useMcpServers(undefined, undefined, {
    enabled: open && otherUserSessionIssuerIds.size > 0,
  });
  const affectedMcpServers = useMemo(() => {
    const servers = serversResult?.mcpServers ?? [];
    return servers.filter(
      (mcpServer) =>
        mcpServer.userSessionIssuerId != null &&
        otherUserSessionIssuerIds.has(mcpServer.userSessionIssuerId),
    );
  }, [serversResult, otherUserSessionIssuerIds]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-[560px] flex-col sm:max-w-[560px]"
      >
        <SheetHeader className="px-6 pt-6 pb-0">
          <SheetTitle className="text-lg font-semibold">
            Modify Authorization Server
          </SheetTitle>
        </SheetHeader>

        {open && (
          <ModifyRemoteIdentityProviderSheetBody
            issuer={issuer}
            primaryClient={primaryClient}
            isLoadingClient={isLoadingClients}
            affectedMcpServers={affectedMcpServers}
            onClose={() => onOpenChange(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ModifyRemoteIdentityProviderSheetBody({
  issuer,
  primaryClient,
  isLoadingClient,
  affectedMcpServers,
  onClose,
}: {
  issuer: RemoteSessionIssuer;
  primaryClient: RemoteSessionClient | undefined;
  isLoadingClient: boolean;
  affectedMcpServers: { id: string; name?: string | undefined }[];
  onClose: () => void;
}) {
  const client = useSdkClient();
  const queryClient = useQueryClient();

  // Migrating saves on its own, independent of the form below, the way the
  // client detail page does it. The project update endpoint requires
  // project:write, so the button hides without it.
  const { hasAnyScope } = useRBAC();
  const migrate = useMutation({
    mutationFn: async (clientId: string) => {
      await client.remoteSessionClients.update({
        updateRemoteSessionClientForm: {
          id: clientId,
          legacyCallbackUrl: false,
        },
      });
    },
    onSuccess: async () => {
      // The client detail page reads the organization-scoped queries, so
      // refresh those as well as this project's list.
      await Promise.all([
        invalidateAllRemoteSessionClients(queryClient, { refetchType: "all" }),
        invalidateAllOrganizationRemoteSessionClient(queryClient, {
          refetchType: "all",
        }),
        invalidateAllOrganizationRemoteSessionClients(queryClient, {
          refetchType: "all",
        }),
      ]);
      toast.success("Client migrated to the new callback URL");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to migrate client",
      );
    },
  });

  // Issuer URL + endpoints + discovery state come from the shared hook. The
  // loaded record seeds the snapshot so the Discover/Reset slot and the
  // URL-change reset behave the same way the Attach sheet does, just with
  // saved data as the baseline.
  const discovery = useIssuerDiscovery({
    issuerUrl: issuer.issuer,
    authorizationEndpoint: issuer.authorizationEndpoint ?? "",
    tokenEndpoint: issuer.tokenEndpoint ?? "",
    registrationEndpoint: issuer.registrationEndpoint ?? "",
    jwksUri: issuer.jwksUri ?? "",
    scopesSupported: issuer.scopesSupported ?? [],
    grantTypesSupported: issuer.grantTypesSupported ?? [],
    authorizationGrantProfilesSupported:
      issuer.authorizationGrantProfilesSupported ?? [],
    responseTypesSupported: issuer.responseTypesSupported ?? [],
    tokenEndpointAuthMethodsSupported:
      issuer.tokenEndpointAuthMethodsSupported ?? [],
    // No `?? []`: null means the saved record never captured the field, and
    // the seeded snapshot must round-trip it as null so saving the sheet does
    // not record "the issuer advertises no PKCE methods".
    codeChallengeMethodsSupported: issuer.codeChallengeMethodsSupported ?? null,
    clientIdMetadataDocumentSupported: issuer.clientIdMetadataDocumentSupported,
    revocationEndpoint: issuer.revocationEndpoint ?? "",
    serviceDocumentation: issuer.serviceDocumentation ?? "",
    opPolicyUri: issuer.opPolicyUri ?? "",
    opTosUri: issuer.opTosUri ?? "",
    userinfoEndpoint: issuer.userinfoEndpoint ?? "",
    introspectionEndpoint: issuer.introspectionEndpoint ?? "",
    introspectionEndpointAuthMethodsSupported:
      issuer.introspectionEndpointAuthMethodsSupported ?? null,
    idTokenSigningAlgValuesSupported:
      issuer.idTokenSigningAlgValuesSupported ?? null,
    claimsSupported: issuer.claimsSupported ?? null,
    backchannelLogoutSupported: issuer.backchannelLogoutSupported ?? null,
    authorizationResponseIssParameterSupported:
      issuer.authorizationResponseIssParameterSupported ?? null,
  });
  const {
    issuerUrl,
    setIssuerUrl,
    authorizationEndpoint,
    tokenEndpoint,
    registrationEndpoint,
    jwksUri,
    setAuthorizationEndpoint,
    setTokenEndpoint,
    setRegistrationEndpoint,
    setJwksUri,
    discoveredSnapshot,
    discoverPending,
    discoverError,
    clearDiscoverError,
    runDiscover,
    handleResetEndpoints,
    resetEndpointState,
    showDiscoverControls,
    showResetControls,
    endpointWarnings,
  } = discovery;

  // Editable display name, seeded from the saved record. The body remounts on
  // each open (the `{open && <Body />}` pattern), so seeding from props here is
  // fresh per open. Submitting an empty string clears the name to NULL on the
  // backend.
  const [name, setName] = useState(issuer.name ?? "");

  // Editable logo, seeded from the saved record like name. The update always
  // sends this field, and "" is the explicit "clear to NULL" sentinel, so the
  // seed keeps an unrelated save from wiping the stored logo.
  const [logoAssetId, setLogoAssetId] = useState(issuer.logoAssetId ?? "");
  // Save is held while a logo upload is in flight: submitting mid-upload
  // would persist the pre-upload value and silently drop the picked logo.
  const [logoUploading, setLogoUploading] = useState(false);

  // Repointing a provider can duplicate an existing one just as creating it
  // can, so the same preflight runs here. Gated on the URL having diverged from
  // what is saved: while they match, the only record it could report is this
  // one. excludeId covers the remaining case, a normalization-equivalent edit
  // (a trailing slash on your own URL) that the shared candidate set still
  // matches.
  const [settledIssuerUrl, setSettledIssuerUrl] = useState(issuer.issuer);
  const { matches: duplicateMatches } = useIssuerDuplicatePreflight({
    issuerUrl: settledIssuerUrl,
    scope: "project",
    // No `open` gate: this body only mounts while the sheet is open, so closing
    // it unmounts the query rather than leaving it live.
    enabled: settledIssuerUrl.trim() !== issuer.issuer,
    excludeId: issuer.id,
  });

  // Client-side form state. clientId is informational only — the API has no
  // rotate path, so it stays read-only. clientSecret starts blank; a typed
  // value rotates the secret for secret-based auth, blank means "leave unchanged".
  const [clientSecret, setClientSecret] = useState("");
  const [tokenEndpointAuthMethod, setTokenEndpointAuthMethod] = useState<
    CreateRemoteSessionClientFormTokenEndpointAuthMethod | ""
  >("");
  const [authAudienceFormat, setAuthAudienceFormat] = useState<
    AuthAudienceFormat | undefined
  >();
  const [scopeOverride, setScopeOverride] = useState("");
  const [audienceOverride, setAudienceOverride] = useState("");

  const handleTokenEndpointAuthMethodChange = (
    method: CreateRemoteSessionClientFormTokenEndpointAuthMethod | "",
  ) => {
    setTokenEndpointAuthMethod(method);
    if (isPrivateKeyJwtAuthMethod(method)) setClientSecret("");
  };

  const modifyMutation = useMutation({
    mutationFn: async () => {
      if (!primaryClient) {
        throw new Error("No primary remote session client to update.");
      }
      const parsedScopes = parseScopes(scopeOverride);
      const trimmedAudience = audienceOverride.trim();

      // Update the remote_session_issuer. The backend now reads an explicit
      // empty string on the four nullable endpoint fields as "clear to
      // NULL"; an omitted field keeps the existing value. Send the trimmed
      // input directly (including "") so blanking out a field in the UI
      // actually clears the saved record — especially registration_endpoint,
      // which is the signal Speakeasy uses for "DCR is supported on this issuer".
      await client.remoteSessionIssuers.update({
        updateRemoteSessionIssuerForm: {
          id: issuer.id,
          issuer: issuerUrl.trim(),
          // Empty string clears the saved display name to NULL, same three-state
          // semantics the backend applies to the nullable endpoint fields.
          name: name.trim(),
          // Same three-state semantics: "" clears the saved logo to NULL.
          logoAssetId,
          authorizationEndpoint: authorizationEndpoint.trim(),
          tokenEndpoint: tokenEndpoint.trim(),
          registrationEndpoint: registrationEndpoint.trim(),
          jwksUri: jwksUri.trim(),
          scopesSupported: discoveredSnapshot?.scopesSupported,
          grantTypesSupported: discoveredSnapshot?.grantTypesSupported,
          // A reset snapshot means a new issuer URL; its profiles are unknown until rediscovery.
          authorizationGrantProfilesSupported:
            discoveredSnapshot?.authorizationGrantProfilesSupported ?? [],
          responseTypesSupported: discoveredSnapshot?.responseTypesSupported,
          tokenEndpointAuthMethodsSupported:
            discoveredSnapshot?.tokenEndpointAuthMethodsSupported,
          // Null (never captured) goes out as undefined so the server keeps
          // NULL; a fresh discovery snapshot is never null and overwrites.
          codeChallengeMethodsSupported:
            discoveredSnapshot?.codeChallengeMethodsSupported ?? undefined,
          // undefined when no snapshot — the server COALESCEs to keep the
          // stored CIMD-support value; a fresh discovery overwrites it.
          clientIdMetadataDocumentSupported:
            discoveredSnapshot?.clientIdMetadataDocumentSupported,
          // Discovery-only, no form inputs. The snapshot is seeded from the saved
          // record, so absent a fresh discovery these round-trip unchanged; a
          // fresh one overwrites them, and "" clears a URL the issuer dropped.
          revocationEndpoint: discoveredSnapshot?.revocationEndpoint,
          serviceDocumentation: discoveredSnapshot?.serviceDocumentation,
          opPolicyUri: discoveredSnapshot?.opPolicyUri,
          opTosUri: discoveredSnapshot?.opTosUri,
          // Endpoints verbatim ("" clears a dropped URL); null seeded = keep.
          userinfoEndpoint: discoveredSnapshot?.userinfoEndpoint,
          introspectionEndpoint: discoveredSnapshot?.introspectionEndpoint,
          introspectionEndpointAuthMethodsSupported:
            discoveredSnapshot?.introspectionEndpointAuthMethodsSupported ??
            undefined,
          idTokenSigningAlgValuesSupported:
            discoveredSnapshot?.idTokenSigningAlgValuesSupported ?? undefined,
          claimsSupported: discoveredSnapshot?.claimsSupported ?? undefined,
          backchannelLogoutSupported:
            discoveredSnapshot?.backchannelLogoutSupported ?? undefined,
          authorizationResponseIssParameterSupported:
            discoveredSnapshot?.authorizationResponseIssParameterSupported ??
            undefined,
        },
      });

      // Update the remote_session_client overrides. Omit clientSecret when
      // private_key_jwt is selected or the input is blank; both keep the
      // stored secret in place.
      await client.remoteSessionClients.update({
        updateRemoteSessionClientForm: {
          id: primaryClient.id,
          clientSecret: clientSecretUpdateValue(
            tokenEndpointAuthMethod,
            clientSecret,
          ),
          tokenEndpointAuthMethod: tokenEndpointAuthMethod || undefined,
          tokenEndpointAuthAudienceFormat: isPrivateKeyJwtAuthMethod(
            tokenEndpointAuthMethod,
          )
            ? authAudienceFormat
            : undefined,
          // Backend update uses COALESCE — omitting (undefined) keeps the
          // stored value, sending an empty array would clear it. Mirror the
          // audience handling here: only send when non-empty so the Modify
          // UI never silently wipes existing overrides. Clearing today
          // requires the operator to detach + re-attach the identity
          // provider with new settings.
          scope: parsedScopes.length > 0 ? parsedScopes : undefined,
          audience: trimmedAudience || undefined,
        },
      });
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateAllRemoteSessionIssuers(queryClient, { refetchType: "all" }),
        invalidateAllRemoteSessionIssuer(queryClient, { refetchType: "all" }),
        invalidateAllRemoteSessionClients(queryClient, { refetchType: "all" }),
        // Also invalidate MCP server queries so the sidebar readiness bar
        // refreshes (AGE-3279).
        invalidateAllGetMcpServer(queryClient, { refetchType: "all" }),
        invalidateAllMcpServers(queryClient, { refetchType: "all" }),
        // Issuer overrides feed the scopes a sign-in would request.
        invalidateAllGetRemoteMcpServerScopes(queryClient),
      ]);
      toast.success("Authorization server updated");
      onClose();
    },
    onError: (error) => {
      console.error("Modify authorization server failed", error);
    },
  });

  const submitting = modifyMutation.isPending;
  const submitError = modifyMutation.error
    ? modifyMutation.error instanceof Error && modifyMutation.error.message
      ? modifyMutation.error.message
      : "An unexpected error occurred. Please try again."
    : null;

  // Seed client-derived fields exactly once per sheet open, the first time
  // the client lookup resolves. The sheet body remounts on close (the
  // `{open && <Body />}` pattern in the parent), so the ref resets naturally
  // on the next open — no need to wire it to the open prop. Guarding via a
  // ref instead of a narrow useEffect dependency keeps the linter happy AND
  // prevents subsequent refetches from clobbering in-progress edits.
  const clientFieldsInitializedRef = useRef(false);
  useEffect(() => {
    if (!primaryClient || clientFieldsInitializedRef.current) return;
    setTokenEndpointAuthMethod(primaryClient.tokenEndpointAuthMethod ?? "");
    setAuthAudienceFormat(primaryClient.tokenEndpointAuthAudienceFormat);
    setScopeOverride((primaryClient.scope ?? []).join(", "));
    setAudienceOverride(primaryClient.audience ?? "");
    clientFieldsInitializedRef.current = true;
  }, [primaryClient]);

  const submittable = !!primaryClient && issuerUrl.trim().length > 0;

  const handleSubmit = () => {
    if (!submittable || submitting || logoUploading || !primaryClient) return;
    modifyMutation.mutate();
  };

  return (
    <>
      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
        {affectedMcpServers.length > 0 && (
          <Alert variant="warning" dismissible={false}>
            <Stack gap={2}>
              <Text className="font-medium">
                Heads up — this authorization server is shared with{" "}
                {affectedMcpServers.length === 1
                  ? "1 other MCP server"
                  : `${affectedMcpServers.length} other MCP servers`}{" "}
                in this project.
              </Text>
              <Text small>
                Issuer URL and endpoint edits apply to every server that uses
                this authorization server. Client credentials, scope, and
                audience changes only affect this server.
              </Text>
              <Text small mono>
                {affectedMcpServers
                  .map((server) => server.name?.trim() || server.id.slice(0, 8))
                  .join(", ")}
              </Text>
            </Stack>
          </Alert>
        )}

        <IssuerUrlField
          issuerUrl={issuerUrl}
          onIssuerUrlSettled={setSettledIssuerUrl}
          duplicateWarning={
            <IssuerDuplicateWarning
              matches={duplicateMatches}
              viewerScope="project"
            />
          }
          onIssuerUrlChange={(value) => {
            setIssuerUrl(value);
            // Any edit invalidates the last blur, so a warning cannot outlive
            // the URL it describes.
            setSettledIssuerUrl("");
            clearDiscoverError();
            // Same reset semantics as Attach: when the URL diverges from the
            // settled state, every downstream field was tied to that prior
            // URL. Clear them so re-Discover (or manual re-entry) produces a
            // coherent result. Client credentials and auth method stay with
            // the existing client record, which the update retains unless
            // the operator explicitly changes them.
            if (discoveredSnapshot && value.trim() !== discoveredSnapshot.url) {
              resetEndpointState();
              setClientSecret("");
              setScopeOverride("");
              setAudienceOverride("");
            }
          }}
        />

        <Stack gap={2}>
          <Label className="text-muted-foreground text-xs">Slug</Label>
          <Text small mono>
            {issuer.slug}
          </Text>
          <Text muted small>
            Slug is the stable identifier for this authorization server and
            can't be renamed here.
          </Text>
        </Stack>

        <Stack gap={2}>
          <Label className="text-muted-foreground text-xs">
            Display name (optional)
          </Label>
          <Input
            value={name}
            onChange={setName}
            placeholder="My Authorization Server"
          />
          <Text muted small>
            Friendly label shown in the dashboard. Clear it to fall back to the
            Issuer URL.
          </Text>
        </Stack>

        <AssetImageUploadField
          tier="project"
          value={logoAssetId}
          onChange={setLogoAssetId}
          onUploadingChange={setLogoUploading}
          description="Shown beside this authorization server in the dashboard and on the connect consent page. Saved with your other changes."
        />

        <EndpointsFields
          issuerUrl={issuerUrl}
          authorizationEndpoint={authorizationEndpoint}
          tokenEndpoint={tokenEndpoint}
          registrationEndpoint={registrationEndpoint}
          jwksUri={jwksUri}
          endpointWarnings={endpointWarnings}
          discoverPending={discoverPending}
          discoverError={discoverError}
          showDiscoverControls={showDiscoverControls}
          showResetControls={showResetControls}
          onAuthorizationEndpointChange={setAuthorizationEndpoint}
          onTokenEndpointChange={setTokenEndpoint}
          onRegistrationEndpointChange={setRegistrationEndpoint}
          onJwksUriChange={setJwksUri}
          onDiscover={() => {
            runDiscover(issuerUrl);
          }}
          onResetEndpoints={handleResetEndpoints}
        />

        {primaryClient && (
          <LegacyCallbackAlert
            legacyCallbackUrl={primaryClient.legacyCallbackUrl}
            callbackUrl={primaryClient.callbackUrl}
            onMigrate={() => migrate.mutate(primaryClient.id)}
            isMigrating={migrate.isPending}
            canMigrate={hasAnyScope(["project:write"])}
          />
        )}

        {isLoadingClient ? (
          <Text muted small>
            Loading client credentials…
          </Text>
        ) : (
          <ClientCredentialsFields
            clientId={primaryClient?.clientId ?? ""}
            clientSecret={clientSecret}
            tokenEndpointAuthMethod={tokenEndpointAuthMethod}
            allowPrivateKeyJwt={primaryClient?.jsonWebKeySetId != null}
            clientIdEditable={false}
            callbackURL={
              primaryClient ? (primaryClient.callbackUrl ?? null) : undefined
            }
            clientSecretLabel="Client Secret (leave blank to keep existing)"
            clientSecretPlaceholder="Type a new secret to rotate"
            onClientIdChange={() => undefined}
            onClientSecretChange={setClientSecret}
            onTokenEndpointAuthMethodChange={
              handleTokenEndpointAuthMethodChange
            }
          />
        )}

        {isPrivateKeyJwtAuthMethod(tokenEndpointAuthMethod) && (
          <ClientAssertionAudienceField
            value={
              authAudienceFormat ??
              UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat.Issuer
            }
            onChange={setAuthAudienceFormat}
          />
        )}

        <OverridesFields
          scopeOverride={scopeOverride}
          audienceOverride={audienceOverride}
          onScopeOverrideChange={setScopeOverride}
          onAudienceOverrideChange={setAudienceOverride}
        />

        {submitError && (
          <Alert variant="error" dismissible={false}>
            {submitError}
          </Alert>
        )}
      </div>

      <SheetFooter className="flex-row items-center justify-end gap-2 border-t px-6 py-4">
        <Button variant="secondary" disabled={submitting} onClick={onClose}>
          <Button.Text>Cancel</Button.Text>
        </Button>
        <Button
          variant="primary"
          disabled={
            !submittable || submitting || logoUploading || isLoadingClient
          }
          onClick={handleSubmit}
        >
          <Button.Text>{submitting ? "Saving…" : "Save"}</Button.Text>
        </Button>
      </SheetFooter>
    </>
  );
}
